import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import api from "services/api";

const PdfSplitPage = () => {
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [mergeWithNextByPage, setMergeWithNextByPage] = useState({});
  const [removedPages, setRemovedPages] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [importing, setImporting] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const loadJobs = async () => {
    if (!selectedCompany?.id) return;
    setLoadingJobs(true);
    try {
      const response = await api.get("/api/pdf-split-jobs/", {
        params: { company_id: selectedCompany.id, status: "pending" },
      });
      const nextJobs = response.data.jobs || [];
      setJobs(nextJobs);
      if (!selectedJobId && nextJobs.length > 0) {
        setSelectedJobId(nextJobs[0].id);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitLoadFailed"), "error");
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadJobDetail = async (jobId) => {
    if (!jobId) return;
    setLoadingDetail(true);
    try {
      const response = await api.get(`/api/pdf-split-jobs/${jobId}/`);
      const job = response.data.job;
      setJobDetail(job);
      setMergeWithNextByPage({});
      setRemovedPages({});
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitLoadFailed"), "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    setJobs([]);
    setSelectedJobId(null);
    setJobDetail(null);
    loadJobs();
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (selectedJobId) loadJobDetail(selectedJobId);
  }, [selectedJobId]);

  const groups = useMemo(() => {
    const result = [];
    let currentGroup = null;
    (jobDetail?.pages || []).forEach((page) => {
      if (removedPages[page.page_number]) return;
      if (!currentGroup) {
        currentGroup = [];
        result.push(currentGroup);
      }
      currentGroup.push(page.page_number);
      if (!mergeWithNextByPage[page.page_number]) {
        currentGroup = null;
      }
    });
    return result;
  }, [jobDetail?.pages, mergeWithNextByPage, removedPages]);

  const lastIncludedPageNumber = useMemo(() => {
    const includedPages = (jobDetail?.pages || []).filter(
      (page) => !removedPages[page.page_number]
    );
    return includedPages.length ? includedPages[includedPages.length - 1].page_number : null;
  }, [jobDetail?.pages, removedPages]);

  const documentNumberByPage = useMemo(() => {
    const mapped = {};
    groups.forEach((group, index) => {
      group.forEach((pageNumber) => {
        mapped[pageNumber] = index + 1;
      });
    });
    return mapped;
  }, [groups]);

  const setEachPageSeparate = () => {
    setMergeWithNextByPage({});
    setRemovedPages({});
  };

  const setAllPagesOneGroup = () => {
    setMergeWithNextByPage(
      (jobDetail?.pages || []).reduce((acc, page, index, pages) => {
        acc[page.page_number] = index < pages.length - 1;
        return acc;
      }, {})
    );
    setRemovedPages({});
  };

  const handleImport = async () => {
    if (!jobDetail?.id || groups.length === 0) {
      showSnackbar(t("pdfSplitNoGroups"), "error");
      return;
    }
    setImporting(true);
    try {
      await api.post(`/api/pdf-split-jobs/${jobDetail.id}/import/`, { groups });
      showSnackbar(t("pdfSplitImported"), "success");
      setJobDetail(null);
      setSelectedJobId(null);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      await loadJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitImportFailed"), "error");
    } finally {
      setImporting(false);
    }
  };

  const handleDiscard = async () => {
    if (!jobDetail?.id) return;
    const confirmed = window.confirm(t("pdfSplitDiscardConfirm"));
    if (!confirmed) return;

    setDiscarding(true);
    try {
      await api.post(`/api/pdf-split-jobs/${jobDetail.id}/cancel/`);
      showSnackbar(t("pdfSplitDiscarded"), "success");
      setJobDetail(null);
      setSelectedJobId(null);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      await loadJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitDiscardFailed"), "error");
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <CallSplitIcon sx={{ color: "white" }} />
                  <MDTypography variant="h6" color="white">
                    {t("pdfSplitQueue")}
                  </MDTypography>
                </Box>
              </MDBox>

              <MDBox p={3}>
                {!selectedCompany?.id && <Alert severity="info">{t("selectCompanyFirst")}</Alert>}

                {selectedCompany?.id && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">{t("pendingPdfSplits")}</Typography>
                        {loadingJobs && <CircularProgress size={18} />}
                      </Box>
                      <Card variant="outlined" sx={{ maxHeight: 620, overflow: "auto" }}>
                        {jobs.length === 0 && !loadingJobs ? (
                          <Box p={2}>
                            <Typography variant="body2" color="text.secondary">
                              {t("noPendingPdfSplits")}
                            </Typography>
                          </Box>
                        ) : (
                          <List dense disablePadding>
                            {jobs.map((job) => (
                              <ListItemButton
                                key={job.id}
                                selected={selectedJobId === job.id}
                                onClick={() => setSelectedJobId(job.id)}
                              >
                                <ListItemText
                                  primary={job.original_filename}
                                  secondary={`${job.page_count} ${t("pages") || "psl."} · ${
                                    job.sender || "-"
                                  }`}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        )}
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      {loadingDetail ? (
                        <Box display="flex" justifyContent="center" py={8}>
                          <CircularProgress />
                        </Box>
                      ) : jobDetail ? (
                        <Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            flexWrap="wrap"
                            mb={2}
                          >
                            <Box>
                              <Typography variant="h6">{jobDetail.original_filename}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {jobDetail.subject || "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {jobDetail.sender || "-"}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {jobDetail.is_advance_report && (
                                <Chip size="small" label={t("advanceReport")} color="warning" />
                              )}
                              <Chip size="small" label={`${groups.length} ${t("documents")}`} />
                            </Box>
                          </Box>

                          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              onClick={setEachPageSeparate}
                            >
                              {t("pdfSplitEachPage")}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              onClick={setAllPagesOneGroup}
                            >
                              {t("pdfSplitAllPages")}
                            </MDButton>
                            <MDButton
                              variant="contained"
                              color="info"
                              startIcon={<DoneIcon />}
                              onClick={handleImport}
                              disabled={importing || discarding || groups.length === 0}
                            >
                              {importing ? t("loading") : t("pdfSplitImportGroups")}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={handleDiscard}
                              disabled={importing || discarding}
                            >
                              {discarding ? t("loading") : t("pdfSplitDiscard")}
                            </MDButton>
                          </Box>

                          <Alert severity="info" sx={{ mb: 2 }}>
                            {t("pdfSplitInstructions")}
                          </Alert>

                          <Grid container spacing={1.5}>
                            {(jobDetail.pages || []).map((page) => {
                              const removed = !!removedPages[page.page_number];
                              const documentNumber = documentNumberByPage[page.page_number];
                              const mergesWithNext = !!mergeWithNextByPage[page.page_number];
                              const canMergeWithNext = page.page_number !== lastIncludedPageNumber;
                              return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={page.id}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: 1.25,
                                      opacity: removed ? 0.45 : 1,
                                      height: "100%",
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={page.preview_url || page.thumbnail_url}
                                      alt={`Page ${page.page_number}`}
                                      sx={{
                                        width: "100%",
                                        aspectRatio: "3 / 4",
                                        objectFit: "contain",
                                        backgroundColor: "grey.100",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                      }}
                                    />
                                    <Divider sx={{ my: 1 }} />
                                    <Box display="flex" flexDirection="column" gap={0.75}>
                                      <Box display="flex" alignItems="center" gap={0.75}>
                                        <Checkbox
                                          checked={!removed}
                                          onChange={(event) =>
                                            setRemovedPages((prev) => ({
                                              ...prev,
                                              [page.page_number]: !event.target.checked,
                                            }))
                                          }
                                          sx={{ p: 0.5 }}
                                        />
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                          {t("page")} {page.page_number}
                                        </Typography>
                                        {!removed && (
                                          <Chip
                                            size="small"
                                            color={mergesWithNext ? "default" : "info"}
                                            variant={mergesWithNext ? "outlined" : "filled"}
                                            label={`${t("document")} ${documentNumber || "-"}`}
                                          />
                                        )}
                                      </Box>
                                      {!removed && canMergeWithNext && (
                                        <FormControlLabel
                                          sx={{
                                            m: 0,
                                            "& .MuiFormControlLabel-label": {
                                              fontSize: "0.78rem",
                                              lineHeight: 1.2,
                                            },
                                          }}
                                          control={
                                            <Switch
                                              size="small"
                                              checked={mergesWithNext}
                                              onChange={(event) =>
                                                setMergeWithNextByPage((prev) => ({
                                                  ...prev,
                                                  [page.page_number]: event.target.checked,
                                                }))
                                              }
                                            />
                                          }
                                          label={t("pdfSplitMergeWithNext")}
                                        />
                                      )}
                                    </Box>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ) : (
                        <Alert severity="info">{t("selectPdfSplitJob")}</Alert>
                      )}
                    </Grid>
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default PdfSplitPage;
